import { useMemo, useState } from 'react'
import { PageHeader } from '@/components/layout'
import { LoadingOrb } from '@/components/ui/loading-orb'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Switch,
} from '@/components/ui'
import { api } from '@/api/client'

const ACTIONS = ['block', 'warn', 'allow']
const MODES = ['deterministic', 'llm', 'hybrid']
const SCOPES = ['account', 'portfolio']
const KINDS = [
  'message_copy',
  'message_volume',
  'calendar_volume',
  'timing',
  'emergency',
  'opt_out',
  'custom',
]

const emptyConfig = { version: 1, rules: [] }

const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

const getRuleTone = (rule) => {
  if (!rule.enabled) return 'outline'
  if (rule.action === 'block') return 'destructive'
  if (rule.allowOverride) return 'secondary'
  return 'outline'
}

function RuleField({ label, children }) {
  return (
    <label className="space-y-1 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
    </label>
  )
}

function NativeSelect({ value, onChange, options }) {
  return (
    <select
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

function RuleEditor({ rule, onChange }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">{rule.title}</CardTitle>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>{rule.id}</span>
              <span>{rule.kind}</span>
              <span>{rule.mode}</span>
            </div>
          </div>
          <Badge variant={getRuleTone(rule)}>{rule.enabled ? rule.action : 'off'}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_140px_140px_140px]">
          <RuleField label="Title">
            <Input
              value={rule.title || ''}
              onChange={(event) => onChange({ title: event.target.value })}
            />
          </RuleField>
          <RuleField label="Kind">
            <NativeSelect
              value={rule.kind}
              options={KINDS}
              onChange={(value) => onChange({ kind: value })}
            />
          </RuleField>
          <RuleField label="Action">
            <NativeSelect
              value={rule.action}
              options={ACTIONS}
              onChange={(value) => onChange({ action: value })}
            />
          </RuleField>
          <RuleField label="Mode">
            <NativeSelect
              value={rule.mode}
              options={MODES}
              onChange={(value) => onChange({ mode: value })}
            />
          </RuleField>
        </div>

        <RuleField label="Plain-English rule">
          <textarea
            value={rule.plainEnglishRule || ''}
            onChange={(event) => onChange({ plainEnglishRule: event.target.value })}
            className="min-h-[76px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </RuleField>

        <div className="grid gap-3 md:grid-cols-6">
          <RuleField label="Scope">
            <NativeSelect
              value={rule.scope || 'account'}
              options={SCOPES}
              onChange={(value) => onChange({ scope: value })}
            />
          </RuleField>
          <RuleField label="Threshold">
            <Input
              value={rule.threshold ?? ''}
              inputMode="numeric"
              onChange={(event) => onChange({ threshold: toNullableNumber(event.target.value) })}
            />
          </RuleField>
          <RuleField label="Lookback days">
            <Input
              value={rule.lookbackDays ?? ''}
              inputMode="numeric"
              onChange={(event) => onChange({ lookbackDays: toNullableNumber(event.target.value) })}
            />
          </RuleField>
          <RuleField label="Override limit">
            <Input
              value={rule.monthlyOverrideLimit ?? ''}
              inputMode="numeric"
              onChange={(event) =>
                onChange({ monthlyOverrideLimit: toNullableNumber(event.target.value) })
              }
            />
          </RuleField>
          <div className="flex items-end gap-2 pb-2">
            <Switch
              checked={Boolean(rule.enabled)}
              onCheckedChange={(checked) => onChange({ enabled: checked })}
            />
            <span className="text-xs font-medium text-muted-foreground">Enabled</span>
          </div>
          <div className="flex items-end gap-2 pb-2">
            <Switch
              checked={Boolean(rule.allowOverride)}
              onCheckedChange={(checked) => onChange({ allowOverride: checked })}
            />
            <span className="text-xs font-medium text-muted-foreground">Override</span>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[260px_minmax(0,1fr)]">
          <RuleField label="Deterministic key">
            <Input
              value={rule.deterministicKey || ''}
              onChange={(event) => onChange({ deterministicKey: event.target.value || null })}
            />
          </RuleField>
          <RuleField label="LLM instruction">
            <textarea
              value={rule.llmInstruction || ''}
              onChange={(event) => onChange({ llmInstruction: event.target.value || null })}
              className="min-h-[76px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </RuleField>
        </div>
      </CardContent>
    </Card>
  )
}

export function AnnouncementRulesPage() {
  const [accountId, setAccountId] = useState('')
  const [portfolioId, setPortfolioId] = useState('')
  const [config, setConfig] = useState(emptyConfig)
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasLoadedRules, setHasLoadedRules] = useState(false)

  const rules = useMemo(() => config?.rules || [], [config])
  const overrideRules = useMemo(
    () => rules.filter((rule) => rule.enabled && rule.action === 'block' && rule.allowOverride),
    [rules],
  )

  const updateRule = (ruleId, patch) => {
    setConfig((current) => ({
      version: 1,
      rules: (current.rules || []).map((rule) =>
        rule.id === ruleId ? { ...rule, ...patch } : rule,
      ),
    }))
  }

  const changeScope = (setter, value) => {
    setter(value)
    setConfig(emptyConfig)
    setHasLoadedRules(false)
    setStatusMessage('')
    setErrorMessage('')
  }

  const loadRules = async () => {
    if (!accountId.trim()) {
      setErrorMessage('Enter an account ID before loading rules.')
      return
    }

    setIsLoading(true)
    setErrorMessage('')
    setStatusMessage('')
    try {
      const query = portfolioId.trim()
        ? `?portfolioId=${encodeURIComponent(portfolioId.trim())}`
        : ''
      const result = await api.get(
        `/announcement-rules/accounts/${encodeURIComponent(accountId.trim())}${query}`,
      )
      setConfig(result.merged || emptyConfig)
      setHasLoadedRules(true)
      setStatusMessage('Rules loaded.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load announcement rules.')
    } finally {
      setIsLoading(false)
    }
  }

  const saveRules = async () => {
    if (!hasLoadedRules || isLoading || isSaving) return

    if (!accountId.trim()) {
      setErrorMessage('Enter an account ID before saving rules.')
      return
    }

    setIsSaving(true)
    setErrorMessage('')
    setStatusMessage('')
    try {
      const result = await api.put(
        `/announcement-rules/accounts/${encodeURIComponent(accountId.trim())}`,
        {
          portfolioId: portfolioId.trim() ? Number(portfolioId.trim()) : null,
          announcementRules: {
            version: 1,
            rules,
          },
        },
      )
      setConfig(result.merged || emptyConfig)
      setStatusMessage('Rules saved.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save announcement rules.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Announcement Rules"
        description="Configure the account and property rules that block customer announcements"
      />
      <div className="flex-1 space-y-4 overflow-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Rule scope</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-[180px_180px_auto]">
            <Input
              value={accountId}
              onChange={(event) => changeScope(setAccountId, event.target.value)}
              placeholder="Account ID"
              inputMode="numeric"
              disabled={isLoading || isSaving}
            />
            <Input
              value={portfolioId}
              onChange={(event) => changeScope(setPortfolioId, event.target.value)}
              placeholder="Portfolio ID optional"
              inputMode="numeric"
              disabled={isLoading || isSaving}
            />
            <div className="flex gap-2">
              <Button type="button" onClick={loadRules} disabled={isLoading || isSaving}>
                {isLoading ? (
                  <LoadingOrb state="searching" size={16} aria-hidden="true" className="mr-2" />
                ) : null}
                Load rules
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={saveRules}
                disabled={!hasLoadedRules || isLoading || isSaving}
              >
                {isSaving ? (
                  <LoadingOrb state="working" size={16} aria-hidden="true" className="mr-2" />
                ) : null}
                Save rules
              </Button>
            </div>
          </CardContent>
        </Card>

        {errorMessage ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}
        {statusMessage ? (
          <Alert>
            <AlertDescription>{statusMessage}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            {rules.length ? (
              rules.map((rule) => (
                <RuleEditor
                  key={rule.id}
                  rule={rule}
                  onChange={(patch) => updateRule(rule.id, patch)}
                />
              ))
            ) : (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">
                  Load an account to edit its announcement rules.
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Override budget</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Blocking rules can allow up to 5 non-emergency overrides per month per property.
                  Emergency sends do not consume this budget, but the LLM must still verify the text
                  sounds urgent.
                </p>
                <div className="space-y-2">
                  {overrideRules.map((rule) => (
                    <div key={rule.id} className="rounded-md border border-border px-3 py-2">
                      <div className="font-medium text-foreground">{rule.title}</div>
                      <div className="mt-1 text-xs">
                        {rule.monthlyOverrideLimit || 0} overrides per month per property
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>First pilot defaults</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>Account defaults: Tier 2/3 volume, emergency review, opt-out bypass review.</p>
                <p>Portfolio defaults: daily frequency, same-day calendar load, repeated events.</p>
                <p>
                  Internal access grants belong in the upcoming WebInternal user management tools.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnnouncementRulesPage
